import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {dropSettingsNoRendererReads} from "#material/nodes/shader/bsdf-principled/validation"
import {versionChain, color} from "@cm/graph-v3"
import {vec3} from "#material/types/vector"
import {z} from "zod/v4-mini"
import {materialClosureOutput} from "#material/nodes/base/material/validation/io"
import type {Distribution, SubsurfaceMethod} from "#material/nodes/shader/bsdf-principled/types"
import {distributionSchema, subsurfaceMethodSchema} from "#material/nodes/shader/bsdf-principled/types"

export interface BsdfPrincipledSockets extends MaterialNodeSockets {
    inputs: {
        alpha: "float" // found in inputs: color,float; found in settings: float; inferred from data: float
        anisotropic: "float" // found in inputs: float,color; found in settings: float; inferred from data: float
        anisotropicRotation: "float" // found in inputs: color,float; found in settings: float; inferred from data: float
        baseColor: "color" // found in inputs: color,float,vector; found in settings: color; inferred from data: float3
        clearcoat: "float" // found in inputs: color; found in settings: float; inferred from data: float
        clearcoatNormal: "normal" // found in inputs: -; found in settings: vector; inferred from data: float3
        clearcoatRoughness: "float" // found in inputs: color; found in settings: float; inferred from data: float
        emission: "color" // found in inputs: color,vector; found in settings: color; inferred from data: float3
        emissionStrength: "float"
        ior: "float" // found in inputs: color; found in settings: float; inferred from data: float
        metallic: "float" // found in inputs: color,float; found in settings: float; inferred from data: float
        normal: "normal" // found in inputs: normal,color,float; found in settings: vector,plain; inferred from data: float3,null
        roughness: "float" // found in inputs: color,float; found in settings: float; inferred from data: float,null
        sheen: "float" // found in inputs: color; found in settings: float; inferred from data: float
        sheenTint: "float" // found in inputs: -; found in settings: float; inferred from data: float
        specular: "float" // found in inputs: color,float; found in settings: float; inferred from data: float
        specularTint: "float" // found in inputs: color; found in settings: float; inferred from data: float
        subsurface: "float" // found in inputs: -; found in settings: float; inferred from data: float
        subsurfaceAnisotropy: "float"
        subsurfaceIor: "float"
        subsurfaceRadius: "vector" // found in inputs: float; found in settings: vector; inferred from data: float3
        subsurfaceScale: "float"
        tangent: "normal" // found in inputs: normal,float; found in settings: vector,plain; inferred from data: float3,null
        transmission: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    settings: {
        alpha: "float" // found in inputs: color,float; found in settings: float; inferred from data: float
        anisotropic: "float" // found in inputs: float,color; found in settings: float; inferred from data: float
        anisotropicRotation: "float" // found in inputs: color,float; found in settings: float; inferred from data: float
        baseColor: "color" // found in inputs: color,float,vector; found in settings: color; inferred from data: float3
        clearcoat: "float" // found in inputs: color; found in settings: float; inferred from data: float
        clearcoatNormal: "normal" // found in inputs: -; found in settings: vector; inferred from data: float3
        clearcoatRoughness: "float" // found in inputs: color; found in settings: float; inferred from data: float
        emission: "color" // found in inputs: color,vector; found in settings: color; inferred from data: float3
        emissionStrength: "float"
        ior: "float" // found in inputs: color; found in settings: float; inferred from data: float
        metallic: "float" // found in inputs: color,float; found in settings: float; inferred from data: float
        normal: "normal" // found in inputs: normal,color,float; found in settings: vector,plain; inferred from data: float3,null
        roughness: "float" // found in inputs: color,float; found in settings: float; inferred from data: float,null
        sheen: "float" // found in inputs: color; found in settings: float; inferred from data: float
        sheenTint: "float" // found in inputs: -; found in settings: float; inferred from data: float
        specular: "float" // found in inputs: color,float; found in settings: float; inferred from data: float
        specularTint: "float" // found in inputs: color; found in settings: float; inferred from data: float
        subsurface: "float" // found in inputs: -; found in settings: float; inferred from data: float
        subsurfaceAnisotropy: "float"
        subsurfaceIor: "float"
        subsurfaceRadius: "vector" // found in inputs: float; found in settings: vector; inferred from data: float3
        subsurfaceScale: "float"
        tangent: "normal" // found in inputs: normal,float; found in settings: vector,plain; inferred from data: float3,null
        transmission: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    settingsExtensions: {
        distribution?: Distribution // found in inputs: -; found in settings: string; inferred from data: string
        subsurfaceMethod?: SubsurfaceMethod // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        bsdf: "closure"
    }
}

// each of these weights switches a group of sockets on, and every one of them does nothing while it is zero
const HAS_ANISOTROPY = {setting: "anisotropic", whenSet: true} as const
const HAS_SPECULAR = {setting: "specular", whenSet: true} as const
const HAS_SHEEN = {setting: "sheen", whenSet: true} as const
const HAS_CLEARCOAT = {setting: "clearcoat", whenSet: true} as const
const HAS_EMISSION = {setting: "emission", whenSet: true} as const
const HAS_SUBSURFACE = {setting: "subsurface", whenSet: true} as const

export const bsdfPrincipledDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.CmBsdfPrincipled,
    },
    nodeClass: "BsdfPrincipled",
    versionChain: versionChain([dropSettingsNoRendererReads]),
    ui: {
        label: "Principled BSDF",
        color: "#2d8a5d",
        width: 175,
        items: [
            // the sockets of the addon's "Colormass BSDF Principled" group, in the order that group declares them
            {name: "bsdf", valueType: "closure", label: "BSDF", output: true},
            {name: "baseColor", valueType: "color", label: "Base Color", input: true, setting: true, defaultValue: {r: 0.8, g: 0.8, b: 0.8}},
            {name: "metallic", valueType: "float", label: "Metallic", input: true, range: {min: 0, max: 1}, setting: true, defaultValue: 0},
            {name: "specular", valueType: "float", label: "Specular", input: true, range: {min: 0, max: 1}, setting: true, defaultValue: 0.5},
            {name: "ior", valueType: "float", label: "IOR", input: true, range: {min: 1, max: 4}, setting: true, defaultValue: 1.5},
            {
                name: "specularTint",
                valueType: "float",
                label: "Specular Tint",
                input: true,
                range: {min: 0, max: 1},
                setting: true,
                defaultValue: 0,
                visibleWhen: HAS_SPECULAR,
            },
            {name: "roughness", valueType: "float", label: "Roughness", input: true, range: {min: 0, max: 1}, setting: true, defaultValue: 0.5},
            {name: "anisotropic", valueType: "float", label: "Anisotropic", input: true, range: {min: 0, max: 1}, setting: true, defaultValue: 0},
            {
                name: "anisotropicRotation",
                valueType: "float",
                label: "Anisotropic Rotation",
                input: true,
                range: {min: 0, max: 1},
                setting: true,
                defaultValue: 0,
                visibleWhen: HAS_ANISOTROPY,
            },
            {name: "alpha", valueType: "float", label: "Alpha", input: true, range: {min: 0, max: 1}, setting: true, defaultValue: 1},
            {name: "normal", valueType: "normal", label: "Normal", input: true, setting: false},
            {name: "sheen", valueType: "float", label: "Sheen", input: true, range: {min: 0, max: 1}, setting: true, defaultValue: 0},
            {
                name: "sheenTint",
                valueType: "float",
                label: "Sheen Tint",
                input: true,
                range: {min: 0, max: 1},
                setting: true,
                defaultValue: 1,
                visibleWhen: HAS_SHEEN,
            },
            {name: "clearcoat", valueType: "float", label: "Clearcoat", input: true, range: {min: 0, max: 1}, setting: true, defaultValue: 0},
            {
                name: "clearcoatRoughness",
                valueType: "float",
                label: "Clearcoat Roughness",
                input: true,
                range: {min: 0, max: 1},
                setting: true,
                defaultValue: 0.03,
                visibleWhen: HAS_CLEARCOAT,
            },
            {name: "clearcoatNormal", valueType: "normal", label: "Clearcoat Normal", input: true, setting: false, visibleWhen: HAS_CLEARCOAT},
            {name: "tangent", valueType: "normal", label: "Tangent", input: true, setting: false, visibleWhen: HAS_ANISOTROPY},
            {name: "transmission", valueType: "float", label: "Transmission", input: true, range: {min: 0, max: 1}, setting: true, defaultValue: 0},
            {name: "emission", valueType: "color", label: "Emission", input: true, setting: true, defaultValue: {r: 0.0, g: 0.0, b: 0.0}},
            {name: "emissionStrength", valueType: "float", label: "Emission Strength", input: true, setting: true, defaultValue: 1, visibleWhen: HAS_EMISSION},
            {name: "subsurface", valueType: "float", label: "Subsurface", input: true, range: {min: 0, max: 1}, setting: true, defaultValue: 0},
            {
                name: "subsurfaceRadius",
                valueType: "vector",
                label: "Subsurface Radius",
                input: true,
                setting: true,
                defaultValue: {x: 1, y: 0.2, z: 0.1},
                visibleWhen: HAS_SUBSURFACE,
            },
            {
                name: "subsurfaceScale",
                valueType: "float",
                label: "Subsurface Scale",
                input: true,
                setting: true,
                defaultValue: 0.05,
                visibleWhen: HAS_SUBSURFACE,
            },
            {
                name: "subsurfaceIor",
                valueType: "float",
                label: "Subsurface IOR",
                input: true,
                range: {min: 1, max: 4},
                setting: true,
                defaultValue: 1.4,
                visibleWhen: HAS_SUBSURFACE,
            },
            {
                name: "subsurfaceAnisotropy",
                valueType: "float",
                label: "Subsurface Anisotropy",
                input: true,
                range: {min: 0, max: 1},
                setting: true,
                defaultValue: 0,
                visibleWhen: HAS_SUBSURFACE,
            },
            {
                defaultValue: "GGX",
                name: "distribution",
                label: "Distribution",
                valueType: "string",
                options: [
                    {value: "GGX", label: "GGX"},
                    {value: "MULTI_GGX", label: "Multiscatter GGX"},
                ],
                input: false,
                setting: true,
            },
            {
                defaultValue: "BURLEY",
                name: "subsurfaceMethod",
                label: "Subsurface Method",
                valueType: "string",
                options: [
                    {value: "BURLEY", label: "Christensen-Burley"},
                    {value: "RANDOM_WALK", label: "Random Walk"},
                ],
                input: false,
                setting: true,
            },
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({bsdf: materialClosureOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({
                alpha: optionalMaterialInput,
                anisotropic: optionalMaterialInput,
                anisotropicRotation: optionalMaterialInput,
                baseColor: optionalMaterialInput,
                clearcoat: optionalMaterialInput,
                clearcoatNormal: optionalMaterialInput,
                clearcoatRoughness: optionalMaterialInput,
                emission: optionalMaterialInput,
                emissionStrength: optionalMaterialInput,
                ior: optionalMaterialInput,
                metallic: optionalMaterialInput,
                normal: optionalMaterialInput,
                roughness: optionalMaterialInput,
                sheen: optionalMaterialInput,
                sheenTint: optionalMaterialInput,
                specular: optionalMaterialInput,
                specularTint: optionalMaterialInput,
                subsurface: optionalMaterialInput,
                subsurfaceAnisotropy: optionalMaterialInput,
                subsurfaceIor: optionalMaterialInput,
                subsurfaceRadius: optionalMaterialInput,
                subsurfaceScale: optionalMaterialInput,
                tangent: optionalMaterialInput,
                transmission: optionalMaterialInput,
            }),
            settings: z.strictObject({
                alpha: z.optional(z.number()),
                anisotropic: z.optional(z.number()),
                anisotropicRotation: z.optional(z.number()),
                baseColor: z.optional(color),
                clearcoat: z.optional(z.number()),
                clearcoatNormal: z.optional(vec3),
                clearcoatRoughness: z.optional(z.number()),
                distribution: z.optional(distributionSchema),
                emission: z.optional(color),
                emissionStrength: z.optional(z.number()),
                ior: z.optional(z.number()),
                metallic: z.optional(z.number()),
                normal: z.optional(vec3),
                roughness: z.optional(z.number()),
                sheen: z.optional(z.number()),
                sheenTint: z.optional(z.number()),
                specular: z.optional(z.number()),
                specularTint: z.optional(z.number()),
                subsurface: z.optional(z.number()),
                subsurfaceAnisotropy: z.optional(z.number()),
                subsurfaceIor: z.optional(z.number()),
                subsurfaceRadius: z.optional(vec3),
                subsurfaceMethod: z.optional(subsurfaceMethodSchema),
                subsurfaceScale: z.optional(z.number()),
                tangent: z.optional(vec3),
                transmission: z.optional(z.number()),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<BsdfPrincipledSockets>
